import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Grid2,
  Skeleton,
  CircularProgress,
  Fade,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DeleteOutline, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import icon from './../../../assets/images/no-image.png';
import { resetEventImageUrlState } from '../store/dashboard.action';
import { useDispatch } from 'react-redux';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: theme.shadows.heavy,
}));

const NavigationContainer = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
});

const CapturedEventComponent = ({
  markers = [],
  onMarkerChange,
  loading,
  signsLoaded,
  handleChangeSign,
  deleteSign,
  selectedMarker,
  isAddSign,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState('left');
  const [currentMarkerIndex, setCurrentMarkerIndex] = useState(0);

  const handleNext = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setSlideDirection('left');
    dispatch(resetEventImageUrlState());

    setTimeout(() => {
      const nextIndex = (currentMarkerIndex + 1) % markers.length;
      setCurrentMarkerIndex(nextIndex);
      onMarkerChange(markers[nextIndex]);
      setIsTransitioning(false);
    }, 150);
  };

  const handlePrevious = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setSlideDirection('right');
    dispatch(resetEventImageUrlState());

    setTimeout(() => {
      const prevIndex = currentMarkerIndex === 0 ? markers.length - 1 : currentMarkerIndex - 1;
      setCurrentMarkerIndex(prevIndex);
      onMarkerChange(markers[prevIndex]);
      setIsTransitioning(false);
    }, 150);
  };

  useEffect(() => {
    const index = markers.findIndex((marker) => marker.id === selectedMarker.id);
    setCurrentMarkerIndex(index !== -1 ? index : 0);
  }, [selectedMarker]);

  return (
    <Grid2 item size={{ xs: 12 }} sx={{ maxHeight: '40%' }}>
      <StyledCard>
        <CardContent
          sx={{
            '&.MuiCardContent-root': {
              padding: '8px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              height: '100%',
            },
          }}
        >
          {/* Navigation Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1,
            }}
          >
            <IconButton
              onClick={handlePrevious}
              disabled={markers.length <= 1 || isTransitioning || isAddSign}
              size="small"
              sx={{
                border: 1,
                borderColor: 'divider',
                minWidth: 40,
                minHeight: 40,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.light',
                },
              }}
            >
              <ChevronLeft />
            </IconButton>

            <Box sx={{ textAlign: 'center', px: isMobile ? 1 : 2 }}>
              <Typography variant="body2" color="textSecondary" fontWeight={500}>
                {markers.length > 0 ? `${currentMarkerIndex + 1} of ${markers.length}` : '0 of 0'}
              </Typography>
            </Box>

            <IconButton
              onClick={handleNext}
              disabled={markers.length <= 1 || isTransitioning || isAddSign}
              size="small"
              sx={{
                border: 1,
                borderColor: 'divider',
                minWidth: 40,
                minHeight: 40,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.light',
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>

          {/* Content with Transition */}
          <NavigationContainer sx={{ flex: 1 }}>
            <Slide direction={slideDirection} in={!isTransitioning} timeout={300}>
              <Box>
                <Fade in={!isTransitioning} timeout={200}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? 2 : 3,
                      alignItems: isMobile ? 'center' : 'flex-start',
                      height: '100%',
                    }}
                  >
                    {/* Sign Information */}
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: isMobile ? 1.5 : 2,
                        width: isMobile ? '100%' : 'auto',
                      }}
                    >
                      {/* Sign Name */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'flex-start' : 'center',
                          gap: isMobile ? 0.5 : 2,
                        }}
                      >
                        <Typography
                          color="textSecondary"
                          variant="body2"
                          sx={{ minWidth: isMobile ? 'auto' : 80, fontWeight: 500 }}
                        >
                          Sign Name:
                        </Typography>
                        <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ fontWeight: 600 }}>
                          {loading ? (
                            <Skeleton width="200px" height="28px" />
                          ) : (
                            `${selectedMarker.name}(${selectedMarker.code})` || 'No marker selected'
                          )}
                        </Typography>
                      </Box>

                      {/* Sign Type */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'flex-start' : 'center',
                          gap: isMobile ? 0.5 : 2,
                        }}
                      >
                        <Typography
                          color="textSecondary"
                          variant="body2"
                          sx={{ minWidth: isMobile ? 'auto' : 80, fontWeight: 500 }}
                        >
                          Sign Type:
                        </Typography>
                        <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ fontWeight: 600 }}>
                          {loading ? (
                            <Skeleton width="150px" height="28px" />
                          ) : (
                            selectedMarker.type || 'N/A'
                          )}
                        </Typography>
                      </Box>

                      {/* Position */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'flex-start' : 'center',
                          gap: isMobile ? 0.5 : 2,
                        }}
                      >
                        <Typography
                          color="textSecondary"
                          variant="body2"
                          sx={{ minWidth: isMobile ? 'auto' : 80, fontWeight: 500 }}
                        >
                          Position:
                        </Typography>
                        <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ fontWeight: 600 }}>
                          {loading ? (
                            <Skeleton width="180px" height="28px" />
                          ) : (
                            selectedMarker.position || 'N/A'
                          )}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Sign Image */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: isMobile ? 100 : 120,
                        minHeight: isMobile ? 100 : 120,
                      }}
                    >
                      {loading ? (
                        <Skeleton
                          variant="rounded"
                          width={isMobile ? 100 : 120}
                          height={isMobile ? 100 : 120}
                        />
                      ) : (
                        <Fade in={!isTransitioning} timeout={400}>
                          <Box
                            sx={{
                              width: isMobile ? 100 : 120,
                              height: isMobile ? 100 : 120,
                              borderRadius: 2,
                              overflow: 'hidden',
                              border: 1,
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.50',
                            }}
                          >
                            <img
                              src={selectedMarker.icon || icon}
                              alt={selectedMarker.name || 'Sign'}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'opacity 0.3s ease-in-out',
                              }}
                              title={selectedMarker.name || 'Sign thumbnail'}
                            />
                          </Box>
                        </Fade>
                      )}
                    </Box>
                  </Box>
                </Fade>
              </Box>
            </Slide>
          </NavigationContainer>

          {/* Action Buttons */}
          <Fade in={!isTransitioning} timeout={200}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'flex-end',
                alignItems: isMobile ? 'stretch' : 'center',
                gap: isMobile ? 1 : 2,
                pt: 1,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                variant="outlined"
                color="success"
                size="small"
                disabled={signsLoaded || markers.length === 0 || isAddSign}
                onClick={handleChangeSign}
                endIcon={signsLoaded && <CircularProgress size="1rem" color="warning" />}
                sx={{ minWidth: isMobile ? 'auto' : 120 }}
                fullWidth={isMobile}
              >
                Change Sign
              </Button>
              <IconButton
                onClick={() => deleteSign()}
                size="small"
                disabled={markers.length === 0 || isAddSign}
                sx={{
                  border: 1,
                  borderColor: 'error.main',
                  alignSelf: isMobile ? 'center' : 'auto',
                  '&:hover': {
                    backgroundColor: 'error.light',
                    borderColor: 'error.dark',
                  },
                  '&:disabled': {
                    borderColor: 'grey.300',
                  },
                }}
              >
                <DeleteOutline color="error" />
              </IconButton>
            </Box>
          </Fade>
        </CardContent>
      </StyledCard>
    </Grid2>
  );
};

export default CapturedEventComponent;

CapturedEventComponent.propTypes = {
  markers: PropTypes.array.isRequired,
  onMarkerChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  signsLoaded: PropTypes.bool.isRequired,
  handleChangeSign: PropTypes.func.isRequired,
  deleteSign: PropTypes.func.isRequired,
  selectedMarker: PropTypes.object.isRequired,
  isAddSign: PropTypes.bool.isRequired,
};
